import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import MetricCard from "./MetricCard";
import TrendCard from "./TrendCard";

import {
  engagementOptions,
  popularOffersData,
  popularOffersOptions,
} from "./chartData";
import TablePanel from "./ListPanel";
import {
  useLazyGetActiveMembersQuery,
  useLazyGetDashboardQuery,
  useLazyGetExpiringTrialsQuery,
  useLazyGetMemberEngagementQuery,
  useLazyGetNewSignupsQuery,
  useLazyGetPopularOffersQuery,
  useLazyGetTodayBookingsQuery,
  useLazyGetTopMembersQuery,
  useLazyGetUpcomingRenewalsQuery,
} from "../../service/dashboard";
import { showError } from "../../constants/toasts";
import {
  DashboardStats,
  ExpiringTrialsStats,
  MemberEngagementStats,
  NewSignupsStats,
  TodayBookingsStats,
  TopMembersStats,
} from "../../types/General";
import { formatAEDNumber } from "../../utils/validations";
import { formatDubaiDateTime } from "../../utils/dateTime";
import Loader from "../../constants/Loader";
import { ChartData } from "chart.js";
import { TableCell } from "@mui/material";
import { USER_STATUS } from "../../utils/enums";
import useAuth from "../../hooks/useAuth";
import { startSignalRConnection } from "../../utils/socket";
import { setMemberFilters } from "../../reducers/memberFiltersSlice";
import { useAppDispatch } from "../../hooks/store";

const Dashboard = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const dispatch = useAppDispatch();
  const goToRevenuePage = () => navigate("/revenue-analytics");
  const [getDashboard] = useLazyGetDashboardQuery();
  const [getNewSignups] = useLazyGetNewSignupsQuery();
  const [getTodayBookings] = useLazyGetTodayBookingsQuery();
  const [getTopMembers] = useLazyGetTopMembersQuery();
  const [getActiveMembers] = useLazyGetActiveMembersQuery();
  const [getExpiringTrials] = useLazyGetExpiringTrialsQuery();
  const [getUpcomingRenewals] = useLazyGetUpcomingRenewalsQuery();
  const [getMemberEngagement] = useLazyGetMemberEngagementQuery();
  const [getPopularOffers] = useLazyGetPopularOffersQuery();

  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(
    null
  );

  const [newSignupsData, setNewSignupsData] = useState<
    NewSignupsStats[] | null
  >(null);
  const [todayBookingsData, setTodayBookingsData] = useState<
    TodayBookingsStats[] | null
  >(null);
  const [topMembersData, setTopMembersData] = useState<
    TopMembersStats[] | null
  >(null);
  const [activeMembersData, setActiveMembersData] = useState<
    TopMembersStats[] | null
  >(null);
  const [expiringTrialsData, setExpiringTrialsData] = useState<
    ExpiringTrialsStats[] | null
  >(null);
  const [upcomingRenewalsData, setUpcomingRenewalsData] = useState<
    any[] | null
  >(null);

  const [memberEngagementData, setMemberEngagementData] =
    useState<MemberEngagementStats | null>(null);
  const [popularOffers, setPopularOffers] = useState<any[]>([]);

  const [newSignupsCount, setNewSignupsCount] = useState(0);
  const [todayBookingsCount, setTodayBookingsCount] = useState(0);
  const [activeMembersCount, setActiveMembersCount] = useState(0);
  const [bookingsTodayCount, setBookingsTodayCount] = useState(0);
  const [expiringTrialsCount, setExpiringTrialsCount] = useState(0);
  const [upcomingRenewalsCount, setUpcomingRenewalsCount] = useState(0);
  const [popularOfferFilters, setPopularOfferFilters] = useState("monthly");
  const [engagementFilters, setEngagementFilters] = useState("monthly");

  const engagementData: ChartData<"bar"> = {
    labels: ["Login", "Booking", "No Activity"],
    datasets: [
      {
        label: "Users",
        data: [
          memberEngagementData?.activeMembers || 0,
          memberEngagementData?.bookingMembers || 0,
          memberEngagementData?.inactiveUsers || 0,
        ],
        backgroundColor: "#66bb6a",
      },
    ],
  };

  const labels = popularOffers?.map((item) => item?.offerName) || [];
  const data = popularOffers?.map((item) => item?.totalBookings) || [];

  const popularOffersData: ChartData<"bar"> = {
    labels: labels,
    datasets: [
      {
        label: "Bookings",
        data: data,
        backgroundColor: "#ef6c00",
      },
    ],
  };

  const getDashboardApi = async () => {
    setLoading(true);
    try {
      const response = await getDashboard({}).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setDashboardData(response?.data || null);
      } else {
        setDashboardData(null);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getNewSignupsApi = async () => {
    setLoading(true);
    try {
      const response = await getNewSignups({}).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setNewSignupsData(response?.data?.items || []);
        setNewSignupsCount(response?.data?.totalCount || 0);
      } else {
        setNewSignupsData([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getTodayBookingsApi = async () => {
    setLoading(true);
    try {
      const response = await getTodayBookings({}).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setTodayBookingsData(response?.data?.items || []);
        setTodayBookingsCount(response?.data?.totalCount || 0);
      } else {
        setTodayBookingsData([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getTopMembersApi = async () => {
    setLoading(true);
    try {
      const response = await getTopMembers({}).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setTopMembersData(response?.data || []);
      } else {
        setTopMembersData([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getActiveMembersApi = async () => {
    setLoading(true);
    try {
      const response = await getActiveMembers({}).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setActiveMembersData(response?.data?.members || []);
        setActiveMembersCount(response?.data?.totalTopMembers || 0);
      } else {
        setActiveMembersData([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getExpiringTrialsApi = async () => {
    setLoading(true);
    try {
      const response = await getExpiringTrials({}).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setExpiringTrialsData(response?.data?.items || []);
        setExpiringTrialsCount(response?.data?.totalCount || 0);
      } else {
        setExpiringTrialsData([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getUpcomingRenewalsApi = async () => {
    setLoading(true);
    try {
      const response = await getUpcomingRenewals({}).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setUpcomingRenewalsData(response?.data?.items || []);
        setUpcomingRenewalsCount(response?.data?.totalCount || 0);
      } else {
        setUpcomingRenewalsData([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getMemberEngagementApi = async () => {
    setLoading(true);
    try {
      const response = await getMemberEngagement({
        filters: engagementFilters,
      }).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setMemberEngagementData(response?.data || null);
      } else {
        setMemberEngagementData(null);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getPopularOffersApi = async () => {
    setLoading(true);
    try {
      const response = await getPopularOffers({
        filters: popularOfferFilters,
      }).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setPopularOffers(response?.data || []);
      } else {
        setPopularOffers([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getDashboardApi();
    getNewSignupsApi();
    getTodayBookingsApi();
    getTopMembersApi();
    getActiveMembersApi();
    getExpiringTrialsApi();
    getUpcomingRenewalsApi();
  }, []);

  useEffect(() => {
    getPopularOffersApi();
  }, [popularOfferFilters]);

  useEffect(() => {
    getMemberEngagementApi();
  }, [engagementFilters]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title">
        <h1>Dashboard</h1>
      </div>
      {/* Metrics Section */}
      <h2 className="subTitle">Key Performance Indicators</h2>
      <div className="stats_boxes gap_m">
        <MetricCard
          title="Total Active Members(Solo)"
          value={dashboardData?.userDetails?.totalSoloMembers || "0"}
          subtext={
            dashboardData?.userDetails?.soloMemberChangePercent || 0 < 0
              ? `↓ ${Math.abs(dashboardData?.userDetails?.soloMemberChangePercent || 0)}% from last month`
              : `↑ ${dashboardData?.userDetails?.soloMemberChangePercent || 0}% from last month`
          }
          onClick={() => {
            dispatch(
              setMemberFilters({
                activeMembers: true,
                membershipType: "Single",
              })
            );
            navigate("/revenue-analytics");
          }}
        />
        <MetricCard
          title="Total Active Members(Duo)"
          value={dashboardData?.userDetails?.totalDuoMembers || "0"}
          subtext={
            dashboardData?.userDetails?.duoMemberChangePercent || 0 < 0
              ? `↓ ${Math.abs(dashboardData?.userDetails?.duoMemberChangePercent || 0)}% from last month`
              : `↑ ${dashboardData?.userDetails?.duoMemberChangePercent || 0}% from last month`
          }
          onClick={() => {
            dispatch(
              setMemberFilters({
                activeMembers: true,
                membershipType: "Duo",
              })
            );
            navigate("/revenue-analytics");
          }}
        />
        <MetricCard
          title="Monthly Revenue(Bookings)"
          value={`AED ${
            dashboardData?.bookingDetails?.thisMonthTotal
              ? formatAEDNumber(dashboardData?.bookingDetails?.thisMonthTotal)
              : "0"
          }`}
          subtext={`Last Month: AED ${dashboardData?.bookingDetails?.lastMonthTotal ? formatAEDNumber(dashboardData?.bookingDetails?.lastMonthTotal) : "0"}`}
          onClick={() => {
            navigate("/revenue-analytics", {
              state: {
                type: "bookingsRevenue",
              },
            });
          }}
        />
        <MetricCard
          title="Monthly Revenue(Subscriptions)"
          value={`AED ${
            dashboardData?.subscriptionDetails?.thisMonthRevenue
              ? formatAEDNumber(
                  dashboardData?.subscriptionDetails?.thisMonthRevenue
                )
              : "0"
          }`}
          subtext={`Last Month: AED ${dashboardData?.subscriptionDetails?.lastMonthRevenue ? formatAEDNumber(dashboardData?.subscriptionDetails?.lastMonthRevenue) : "0"}`}
          onClick={() => {
            navigate("/transactions");
          }}
        />

        <MetricCard
          title="Trial Conversions"
          value={`${dashboardData?.conversionResult?.conversionRate || "0"} %`}
          subtext="This month"
          onClick={() => {}}
        />
      </div>

      {/* Today's Activity */}
      <h2 className="subTitle">Today's Activity</h2>
      <div className="grid_sc gap_m">
        <TablePanel
          title="New Signups (Last 30 Days)"
          columns={[
            { label: "Name", key: "name" },
            { label: "Phone", key: "phoneNumber" },
            { label: "Membership Type", key: "membershipType" },
            { label: "Date", key: "createdDate" },
          ]}
          data={newSignupsData || []}
          onViewMore={() => {
            dispatch(setMemberFilters({ newSignupMembers: true }));
            navigate("/revenue-analytics");
          }}
          onCellClick={(key, row) => {
            if (key === "name") {
              navigate(`/manage-customers/detail/${row?.id}`, {
                state: {
                  status:
                    row?.userStatus === USER_STATUS.Approved
                      ? "APPROVED"
                      : row?.userStatus === USER_STATUS.Rejected
                        ? "REJECTED"
                        : row?.userStatus === USER_STATUS.Expired
                          ? "EXPIRED"
                          : row?.userStatus === USER_STATUS.Cancelled
                            ? "CANCELLED"
                            : "PENDING",
                  memberType: row?.membershipType,
                },
              });
            }
          }}
          renderCell={(key, value, row) => {
            if (key === "name") {
              return (
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    maxWidth: 120,
                  }}
                >
                  {value}
                </span>
              );
            }

            if (key === "phoneNumber") {
              const dial = row?.countryDialCode
                ? `+${row?.countryDialCode}`
                : "";

              return (
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    maxWidth: 120,
                  }}
                >
                  {`${dial ? dial + " " : ""}${value || "-"}`}
                </span>
              );
            }

            if (key === "createdDate" && value) {
              return (
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    maxWidth: 180,
                  }}
                >
                  {formatDubaiDateTime(value)}
                </span>
              );
            }

            return value || "-";
          }}
          count={newSignupsCount}
        />

        <TablePanel
          title="Top Five Members"
          onCellClick={(key, row) => {
            if (key === "name") {
              navigate(`/manage-customers/detail/${row?.id}`, {
                state: {
                  status:
                    row?.userStatus === USER_STATUS.Approved
                      ? "APPROVED"
                      : row?.userStatus === USER_STATUS.Rejected
                        ? "REJECTED"
                        : row?.userStatus === USER_STATUS.Expired
                          ? "EXPIRED"
                          : row?.userStatus === USER_STATUS.Cancelled
                            ? "CANCELLED"
                            : "PENDING",
                  memberType: row?.membershipType,
                },
              });
            }
          }}
          columns={[
            { label: "User", key: "name" },

            { label: "Membership Type", key: "membershipType" },
            { label: "Membership Status", key: "memberShip" },
            { label: "bookings", key: "totalBookings" },
          ]}
          data={topMembersData || []}
          count={5}
          renderCell={(key, value) => {
            if (key === "name") {
              return (
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    maxWidth: 120,
                  }}
                >
                  {value}
                </span>
              );
            }

            return value || "-";
          }}
        />
        <TablePanel
          title="Active Members ( For this month )"
          onViewMore={() => {
            dispatch(setMemberFilters({ activeMembersDays: true }));
            navigate("/revenue-analytics");
          }}
          onCellClick={(key, row) => {
            if (key === "name") {
              navigate(`/manage-customers/detail/${row?.id}`, {
                state: {
                  status:
                    row?.userStatus === USER_STATUS.Approved
                      ? "APPROVED"
                      : row?.userStatus === USER_STATUS.Rejected
                        ? "REJECTED"
                        : row?.userStatus === USER_STATUS.Expired
                          ? "EXPIRED"
                          : row?.userStatus === USER_STATUS.Cancelled
                            ? "CANCELLED"
                            : "PENDING",
                  memberType: row?.membershipType,
                },
              });
            }
          }}
          columns={[
            { label: "User", key: "name" },

            { label: "Membership Type", key: "membershipType" },
            { label: "Membership Status", key: "memberShip" },
            { label: "Login Status", key: "loginStatus" },
            { label: "Last Login", key: "lastLogin" },
          ]}
          renderCell={(key, value, row) => {
            if (key === "name") {
              return (
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    maxWidth: 120,
                  }}
                >
                  {value}
                </span>
              );
            }

            if (key === "lastLogin" && value) {
              return (
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    maxWidth: 180,
                  }}
                >
                  {formatDubaiDateTime(value)}
                </span>
              );
            }

            return value || "-";
          }}
          data={activeMembersData || []}
          count={activeMembersCount}
        />

        <TablePanel
          title="Today's Bookings"
          columns={[
            { label: "Offer Name", key: "offerName" },
            { label: "Booking Time", key: "bookingDate" },
            { label: "Client Name", key: "clientName" },
            { label: "Category", key: "categoryName" },
          ]}
          onCellClick={(key, row) => {
            if (key === "offerName") {
              navigate(`/manage-booking-resvp/booking-detail/${row?.id}`);
            }
          }}
          data={todayBookingsData || []}
          count={bookingsTodayCount}
          renderCell={(key, value, row) => {
            if (key === "bookingDate" && value) {
              return formatDubaiDateTime(value);
            }
            if (key === "clientName" && value) {
              return value?.name;
            }
            // if (key === "offer") {
            //   return (
            //     <span
            //       style={{
            //         color: "#BC8F4B",
            //         cursor: "pointer",
            //         textDecoration: "underline",
            //       }}
            //       onClick={() => navigate("/manage-offer/preview-offer-detail")}
            //     >
            //       {value}
            //     </span>
            //   );
            // }
            return value;
          }}
        />

        <TablePanel
          title="Expiring Trials"
          columns={[
            { label: "Name", key: "name" },
            { label: "Membership Type", key: "memberShipType" },
            { label: "Membership Status", key: "membership" },
            { label: "Ends On", key: "endsOn" },
          ]}
          onCellClick={(key, row) => {
            if (key === "name") {
              navigate(`/manage-customers/detail/${row?.id}`, {
                state: {
                  status:
                    row?.userStatus === USER_STATUS.Approved
                      ? "APPROVED"
                      : row?.userStatus === USER_STATUS.Rejected
                        ? "REJECTED"
                        : row?.userStatus === USER_STATUS.Expired
                          ? "EXPIRED"
                          : row?.userStatus === USER_STATUS.Cancelled
                            ? "CANCELLED"
                            : "PENDING",
                  memberType: row?.memberShipType,
                },
              });
            }
          }}
          onViewMore={() => {
            dispatch(
              setMemberFilters({ membership: "Trial", expiringInDays: true })
            );
            navigate("/revenue-analytics");
          }}
          renderCell={(key, value, row) => {
            if (key === "endsOn" && value) {
              return formatDubaiDateTime(value);
            }

            return value || "-";
          }}
          data={expiringTrialsData || []}
          count={expiringTrialsCount}
        />

        <TablePanel
          title="Upcoming Renewals"
          columns={[
            { label: "Name", key: "name" },
            { label: "Membership Status", key: "membership" },
            { label: "Renewal Date", key: "renewalDate" },
          ]}
          onViewMore={() => {
            dispatch(
              setMemberFilters({ membership: "Paid", expiringInDays: true })
            );
            navigate("/revenue-analytics");
          }}
          renderCell={(key, value, row) => {
            if (key === "renewalDate" && value) {
              return formatDubaiDateTime(value);
            }

            return value || "-";
          }}
          data={upcomingRenewalsData || []}
          count={upcomingRenewalsCount}
        />
      </div>

      {/* Alerts */}
      {/* <h2 className="subTitle">Alerts & Action Items</h2>
      <Grid container spacing={3} mb={5}>
        <Grid item xs={12} md={4}>
          <AlertCard title="Payment Failures" items={['Mark - failed 10 July']} />
        </Grid>
        <Grid item xs={12} md={4}>
          <AlertCard title="Inactive Members" items={['Alex - no login for 45 days']} />
        </Grid>
        <Grid item xs={12} md={4}>
          <AlertCard title="System Issues" items={['API timeout on bookings']} />
        </Grid>
      </Grid> */}

      {/* Trends */}
      <h2 className="subTitle">Trends</h2>
      <div className="grid_sc gap_m">
        <TrendCard
          title="Most Popular Offers"
          chartType="bar"
          chartData={popularOffersData}
          chartOptions={popularOffersOptions}
          timeFilters
          filters={popularOfferFilters}
          setFilters={setPopularOfferFilters}
        />

        <TrendCard
          title="Member Engagement"
          chartType="bar"
          chartData={engagementData}
          chartOptions={engagementOptions}
          timeFilters
          filters={engagementFilters}
          setFilters={setEngagementFilters}
        />
      </div>
    </MainContainer>
  );
};

export default Dashboard;
